import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Megaphone, Trash2, Calendar, Crown, ExternalLink, Loader2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { slugForCategory } from "@/lib/categoryHeroes";

/**
 * BannerAdsCard
 * Dashboard widget — lets a paid-tier business owner schedule banner ads.
 *
 * Tier rules (mirrored on the backend in banner_ads.TIER_LIMITS):
 *  - free       → upsell card, no form
 *  - standard   → up to 5 days, home placement only
 *  - premium    → up to 11 days, any placement (home + category:<slug>)
 *
 * Businesses can pick their start date (must be today or within 60 days
 * out). Days always run CONSECUTIVELY from that date.
 */
export default function BannerAdsCard({ business, categories = [] }) {
  const [tierInfo, setTierInfo] = useState(null);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    image_url: "",
    link_url: "",
    alt_text: "",
    placement: "home",
    days: 5,
    starts_on: "", // YYYY-MM-DD; empty = start now
  });

  const tier = useMemo(() => {
    if (!business) return "free";
    if (business.subscription_status !== "active") return "free";
    return business.subscription_tier || "free";
  }, [business]);

  const limits = tierInfo?.limits?.[tier] || tierInfo?.limits?.free;
  const canRunAds = !!limits && limits.max_days > 0;
  const allowCategory = limits?.allowed_placements?.includes("category");

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    // When tier changes (or first load), clamp form defaults
    if (limits?.max_days) {
      setForm((f) => ({
        ...f,
        days: Math.min(f.days || limits.max_days, limits.max_days),
        placement: allowCategory ? f.placement : "home",
      }));
    }
  }, [tier, tierInfo, limits, allowCategory]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tierRes, mineRes] = await Promise.all([
        axios.get(`${API}/banner-ads/tier-info`),
        axios.get(`${API}/banner-ads/mine`, { withCredentials: true }),
      ]);
      setTierInfo(tierRes.data);
      setMine(mineRes.data?.items || []);
    } catch (e) {
      // Non-fatal — likely auth issue elsewhere
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!business) {
      toast.error("Create a business first to run banner ads.");
      return;
    }
    if (!form.image_url) {
      toast.error("Please upload a banner image.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/banner-ads`,
        {
          business_id: business.business_id,
          image_url: form.image_url,
          link_url: form.link_url || null,
          alt_text: form.alt_text || business.name,
          placement: form.placement,
          days: Number(form.days),
          starts_on: form.starts_on || null,
        },
        { withCredentials: true }
      );
      const startsNow = !form.starts_on;
      toast.success(
        startsNow
          ? "Banner scheduled. It's live now."
          : `Banner scheduled to start ${form.starts_on} and run ${form.days} consecutive days.`
      );
      setForm((f) => ({ ...f, image_url: "", link_url: "", alt_text: "", starts_on: "" }));
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule banner");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ad_id) => {
    if (!window.confirm("Delete this banner? It will stop showing immediately.")) return;
    try {
      await axios.delete(`${API}/banner-ads/${ad_id}`, { withCredentials: true });
      toast.success("Banner removed.");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <Card className="border-tbc-gold/20">
        <CardHeader>
          <CardTitle className="font-heading text-tbc-navy flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-tbc-gold" /> Banner Ads
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 font-body flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        </CardContent>
      </Card>
    );
  }

  // Upsell state for Free tier
  if (!canRunAds) {
    return (
      <Card className="border-tbc-gold/40 bg-gradient-to-br from-tbc-cream to-white" data-testid="banner-ads-upsell">
        <CardHeader>
          <CardTitle className="font-heading text-tbc-navy flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-tbc-gold" /> Banner Ads
            <span className="ml-2 inline-flex items-center gap-1 text-xs bg-tbc-gold/15 text-tbc-navy px-2 py-0.5 rounded-full border border-tbc-gold/40">
              Standard / Premium perk
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 font-body">
            Spotlight your business with a homepage banner. Standard members get 5 consecutive days,
            Premium gets 11 consecutive days and can target category pages too. Pick your own start date.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 mt-4 text-tbc-gold font-body font-semibold hover:underline"
            data-testid="banner-ads-upgrade-link"
          >
            See pricing <ExternalLink className="w-4 h-4" />
          </a>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-tbc-gold/30" data-testid="banner-ads-card">
      <CardHeader>
        <CardTitle className="font-heading text-tbc-navy flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-tbc-gold" />
          Banner Ads
          <span className="ml-2 inline-flex items-center gap-1 text-xs bg-tbc-navy/5 text-tbc-navy px-2 py-0.5 rounded-full">
            {tier === "premium" || tier === "founding" ? (
              <Crown className="w-3 h-3 text-tbc-gold" />
            ) : null}
            {limits.label}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Create form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="font-body text-tbc-navy">Banner image (recommended 1600×400)</Label>
            <ImageUpload
              currentImageUrl={form.image_url}
              onUploadSuccess={(url) => setForm({ ...form, image_url: url || "" })}
              imageType="business"
            />
          </div>

          <div>
            <Label htmlFor="banner-link" className="font-body text-tbc-navy">
              Click-through URL (optional)
            </Label>
            <Input
              id="banner-link"
              placeholder="https://yourbusiness.com/promo"
              value={form.link_url}
              onChange={(e) => setForm({ ...form, link_url: e.target.value })}
              data-testid="banner-link-input"
            />
          </div>

          <div>
            <Label htmlFor="banner-alt" className="font-body text-tbc-navy">
              Alt text
            </Label>
            <Input
              id="banner-alt"
              placeholder={business?.name || "Brand banner"}
              value={form.alt_text}
              onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              data-testid="banner-alt-input"
            />
          </div>

          <div>
            <Label className="font-body text-tbc-navy">Placement</Label>
            <Select
              value={form.placement}
              onValueChange={(v) => setForm({ ...form, placement: v })}
            >
              <SelectTrigger data-testid="banner-placement-select">
                <SelectValue placeholder="Choose placement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">Homepage</SelectItem>
                {allowCategory &&
                  categories
                    .map((c) => {
                      const name = typeof c === "string" ? c : c.name;
                      const slug = slugForCategory(name);
                      return slug ? { name, slug } : null;
                    })
                    .filter(Boolean)
                    .map(({ name, slug }) => (
                      <SelectItem key={slug} value={`category:${slug}`}>
                        Category — {name}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
            {!allowCategory && (
              <p className="text-xs text-gray-500 mt-1">Upgrade to Premium to target category pages.</p>
            )}
          </div>

          <div>
            <Label htmlFor="banner-days" className="font-body text-tbc-navy">
              Duration (days)
            </Label>
            <Input
              id="banner-days"
              type="number"
              min={1}
              max={limits.max_days}
              value={form.days}
              onChange={(e) =>
                setForm({
                  ...form,
                  days: Math.max(1, Math.min(limits.max_days, Number(e.target.value) || 1)),
                })
              }
              data-testid="banner-days-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Up to {limits.max_days} days on your tier. Days run <strong>consecutively</strong>.
            </p>
          </div>

          <div>
            <Label htmlFor="banner-starts-on" className="font-body text-tbc-navy">
              Start date <span className="text-gray-400">(optional)</span>
            </Label>
            <Input
              id="banner-starts-on"
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              max={new Date(Date.now() + 60 * 86400 * 1000).toISOString().slice(0, 10)}
              value={form.starts_on}
              onChange={(e) => setForm({ ...form, starts_on: e.target.value })}
              data-testid="banner-starts-on-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              {form.starts_on
                ? `Runs ${form.days} consecutive days starting ${form.starts_on}.`
                : "Leave blank to start immediately. Otherwise up to 60 days out."}
            </p>
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting || !form.image_url}
          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6"
          data-testid="banner-schedule-btn"
        >
          {submitting ? "Scheduling…" : "Schedule banner"}
        </Button>

        {/* Live + scheduled list */}
        <div>
          <h4 className="font-heading text-tbc-navy text-base mb-2">Your banners</h4>
          {mine.length === 0 ? (
            <p className="text-sm text-gray-500 font-body">You haven&apos;t scheduled any banners yet.</p>
          ) : (
            <div className="space-y-3">
              {mine.map((ad) => (
                <div
                  key={ad.ad_id}
                  className="flex items-center gap-4 p-3 border border-gray-100 rounded-lg bg-white"
                  data-testid={`banner-row-${ad.ad_id}`}
                >
                  <img
                    src={ad.image_url?.startsWith("/uploads") ? `/api${ad.image_url}` : ad.image_url}
                    alt={ad.alt_text}
                    className="w-32 h-16 object-cover rounded-md bg-gray-100"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-tbc-navy">
                      <span className="font-semibold capitalize">{ad.status}</span>
                      <span className="text-gray-400"> · </span>
                      <span className="capitalize">{ad.placement}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(ad.starts_at).toLocaleDateString()} →{" "}
                      {new Date(ad.ends_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {ad.impressions || 0} impressions · {ad.clicks || 0} clicks
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-400 hover:text-red-500"
                    onClick={() => handleDelete(ad.ad_id)}
                    data-testid={`banner-delete-${ad.ad_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
